import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import CorgeeModal from './CorgeeModal';
import type { CorgeeModalRef } from './CorgeeModal';
import type { Attachment } from '../../types/conversation';
import type { ApiConfig, CorgeeFunctions } from '../ConversationItem/types';
import './PdfModal.css';

export interface PdfModalRef {
  openModal: (callback?: () => void) => void;
  closeModal: () => void;
}

export interface PdfModalProps {
  openModal?: boolean;
  url?: string;
  attachment: Attachment | Attachment[];
  onFileDownload:
    | ((
        e: React.MouseEvent,
        attachment: Attachment,
        api: ApiConfig,
        corgeeFunctions?: CorgeeFunctions
      ) => void)
    | string;
  title?: string;
  corgeeFunctions?: CorgeeFunctions;
  api: ApiConfig;
  onClose?: () => void;
  isDraggable?: boolean;
  closeOnEscape?: boolean;
  height?: number;
  width?: number | string;
  hasMaxHeight?: boolean;
  allowSaveAndNewCheckBox?: boolean;
  hideActionButtons?: boolean;
  className?: string;
}

const PdfModal = forwardRef<PdfModalRef, PdfModalProps>(
  (
    {
      openModal = false,
      attachment,
      onFileDownload,
      title = '',
      corgeeFunctions,
      api,
      onClose,
      isDraggable = false,
      closeOnEscape = false,
      width = '190vh',
      hasMaxHeight = false,
      hideActionButtons = true,
      className = '',
    },
    ref,
  ) =>
  {
    const [iframeSrc, setIframeSrc] = useState<string>('');
    const corgeeModalRef = useRef<CorgeeModalRef>(null);

    useImperativeHandle(ref, () => ({
      openModal: (callback?: () => void) =>
      {
        corgeeModalRef.current?.openModal(callback);
      },
      closeModal: () =>
      {
        corgeeModalRef.current?.closeModal();
      },
    }));

    // When modal opens, set iframe src from the attachment's FileUrl
    useEffect(() =>
    {
      if (openModal)
      {
        const att = Array.isArray(attachment) ? attachment[0] : attachment;
        if (att?.FileUrl)
        {
          setIframeSrc(att.FileUrl);
        }
      }
      else
      {
        setIframeSrc('');
      }
    }, [openModal, attachment]);

    const handleClose = (): void =>
    {
      setIframeSrc('');
      if (onClose && typeof onClose === 'function')
      {
        onClose();
      }
    };

    const handleOpenInNewWindow = (e: React.MouseEvent): void =>
    {
      e.preventDefault();
      const att = Array.isArray(attachment) ? attachment[0] : attachment;
      if (att)
      {
        window.open(`${window.location.origin}/Dashboard/FilePdf/#/${att.FileName}`, '_blank');
      }
    };

    const handleDownload = (e: React.MouseEvent): void =>
    {
      e.preventDefault();
      const att = Array.isArray(attachment) ? attachment[0] : attachment;
      if (att && typeof onFileDownload === 'function')
      {
        onFileDownload(e, att, api, corgeeFunctions);
      }
    };

    const att = Array.isArray(attachment) ? attachment[0] : attachment;
    const description = openModal ? att?.Description || '' : '';

    // "Open in new tab" icon rendered via headerExtra
    const headerExtra = (
      <a
        id="NewTabPdfLink"
        href="#"
        title="Open in a new tab"
        className="pdf-modal__open-new-tab"
        onClick={handleOpenInNewWindow}
      >
        <i id="NewTabPdf" className="material-icons">
          open_in_new
        </i>
      </a>
    );

    return (
      <CorgeeModal
        ref={corgeeModalRef}
        className={`pdf-modal ${className}`}
        openModal={openModal}
        title={title}
        headerExtra={headerExtra}
        onClose={handleClose}
        isDraggable={isDraggable}
        closeOnEscape={closeOnEscape}
        width={width}
        hasMaxHeight={hasMaxHeight}
        hideActionButtons={hideActionButtons}
      >
        <div className="pdf-modal__viewer-wrapper">
          <iframe className="pdf-modal__iframe" src={iframeSrc} title="PDF Viewer" />
        </div>

        <div className="pdf-modal__footer">
          <h4 className="pdf-modal__description">{description}</h4>
          <a className="pdf-modal__download-link" href="#" onClick={handleDownload}>
            Download
          </a>
        </div>
      </CorgeeModal>
    );
  },
);

PdfModal.displayName = 'PdfModal';

export default PdfModal;
